import { z } from 'zod';
import { daysBetween } from './dates';
import { profitMargin } from './format';
import { type Cents, fromCents, percentOf } from './money';
import { computeSheet, type SheetData } from './timesheets';

/**
 * Project indicators (FR-PRJ-017…023) — the project card and the overview show the same strip,
 * computed here. `null` = "nav datu". Money is returned as 2-decimal strings.
 */
export const INDICATOR_TONES = ['green', 'yellow', 'red', 'grey'] as const;
export type IndicatorTone = (typeof INDICATOR_TONES)[number];
const tone = z.enum(INDICATOR_TONES);

export const projectIndicatorsSchema = z.object({
  /** Time %: elapsed / planned days (FR-PRJ-018); `pct` may exceed 100 (the bar does not). */
  time: z.object({ pct: z.number(), passed: z.number(), total: z.number(), tone }).nullable(),
  /** Scope = completion (FR-PRJ-019). */
  scope: z.object({ pct: z.number() }).nullable(),
  /** Income: invoiced / project revenue, from Fact (FR-PRJ-020). */
  income: z.object({ pct: z.number(), invoiced: z.string(), revenue: z.string(), tone }).nullable(),
  /** Expenses: Fact costs / Plan costs (FR-PRJ-021). */
  expenses: z.object({ pct: z.number(), fact: z.string(), plan: z.string(), tone }).nullable(),
  /** Profit = invoiced − costs (Fact); bar = |profit| / revenue (FR-PRJ-022). */
  profit: z.object({ amount: z.string(), bar: z.number(), tone }).nullable(),
  /** Margin = invoiced / costs (Q14). */
  margin: z.object({ pct: z.number(), tone }).nullable(),
});
export type ProjectIndicators = z.infer<typeof projectIndicatorsSchema>;

export interface IndicatorProject {
  startDate: string | null;
  plannedFinish: string | null;
  completion: number | null;
}

const clamp = (v: number) => Math.max(0, Math.min(100, v));

export function computeIndicators(
  project: IndicatorProject,
  plan: SheetData | null,
  fact: SheetData | null,
  today: string,
): ProjectIndicators {
  const scope = project.completion === null ? null : { pct: project.completion };

  let time: ProjectIndicators['time'] = null;
  if (project.startDate && project.plannedFinish) {
    const total = Math.max(1, daysBetween(project.startDate, project.plannedFinish));
    const passed = Math.max(0, daysBetween(project.startDate, today));
    const pct = (passed / total) * 100;
    const t: IndicatorTone = scope !== null && pct > scope.pct ? 'red' : pct > 50 ? 'yellow' : 'green';
    time = { pct, passed, total, tone: t };
  }

  const factFigures = fact ? computeSheet(fact) : null;
  const planFigures = plan ? computeSheet(plan) : null;
  const invoiced: Cents = factFigures?.amounts.invoiced.total ?? 0;
  const revenue: Cents = factFigures?.amounts.projectRevenue.total ?? 0;
  const factCosts: Cents = factFigures?.costs.total ?? 0;
  const planCosts: Cents = planFigures?.costs.total ?? 0;

  let income: ProjectIndicators['income'] = null;
  if (factFigures && (invoiced !== 0 || revenue !== 0)) {
    const pct = percentOf(invoiced, revenue);
    income = {
      pct,
      invoiced: fromCents(invoiced),
      revenue: fromCents(revenue),
      tone: revenue > invoiced ? 'red' : pct < 50 ? 'yellow' : 'green',
    };
  }

  let expenses: ProjectIndicators['expenses'] = null;
  if (planFigures && factFigures && planCosts !== 0) {
    const pct = (factCosts / planCosts) * 100;
    expenses = {
      pct,
      fact: fromCents(factCosts),
      plan: fromCents(planCosts),
      tone: pct > 100 ? 'red' : pct < 50 ? 'yellow' : 'green',
    };
  }

  const profitCents = invoiced - factCosts;
  const hasProfit = !!factFigures && (profitCents !== 0 || revenue !== 0);
  const profit: ProjectIndicators['profit'] = hasProfit
    ? {
        amount: fromCents(profitCents),
        bar: revenue > 0 ? clamp(percentOf(Math.abs(profitCents), revenue)) : 0,
        tone: profitCents < 0 ? 'red' : 'green',
      }
    : null;
  const marginPct = profitMargin(invoiced, factCosts);
  const margin: ProjectIndicators['margin'] = hasProfit
    ? { pct: marginPct, tone: marginPct <= 0 ? 'grey' : 'green' }
    : null;

  return { time, scope, income, expenses, profit, margin };
}

/** Indicators a reader without `finance.view` may see (ADR 0004): time and scope only. */
export const withoutFinance = (i: ProjectIndicators): ProjectIndicators => ({
  ...i,
  income: null,
  expenses: null,
  profit: null,
  margin: null,
});
